<script setup lang="ts">
import { useConfigStore } from '@core/stores/config'
import { ref, watch } from 'vue'
import CampSearchFilter from '@/views/front-pages/landing-page/camp-search-filter.vue'
import CampsListing from '@/views/front-pages/landing-page/camps-listing.vue'
import { logApiError } from '~/utils/logger'

// Local interfaces matching what the composables return
interface AgeGroup {
  id: string
  name: string
  slug: string
  description?: string | null
  sort_order: number | null
  is_active: boolean | null
  created_at: string | null
  updated_at: string | null
}

interface Period {
  id: string
  name: string
  slug: string
  description?: string | null
  sort_order: number | null
  is_active: boolean | null
  created_at: string | null
  updated_at: string | null
}

interface Category {
  id: string
  name: string
  slug: string
  description: string | null
  display_order: number | null
  is_active: boolean | null
  icon: string | null
  color: string | null
  created_at: string | null
  updated_at: string | null
}

interface CampFilters {
  query?: string
  age?: string
  periode?: string
  category?: string
  location?: string
  sortBy?: string
  priceRange?: [number, number]
}

const store = useConfigStore()

store.skin = 'default'

definePageMeta({
  layout: 'front-page',
  public: true,
  pageTitle: {
    title: 'Onze Kampen',
    subtitle: 'ontdek hier al onze geweldige kampen',
    icon: 'bx-calendar',
    iconColor: 'primary',
  },
})

// SEO Configuration
const { setSeoMeta, getBreadcrumbSchema } = useSeo()

setSeoMeta({
  title: 'Alle Kampen - Overzicht Kinderkampen',
  description: 'Bekijk al onze beschikbare kampen voor kinderen en jongeren. Filter op leeftijd, periode of categorie. Zomerkampen, sportkampen, creatieve kampen en meer!',
  path: '/kampen',
  image: '/images/og-kampen.jpg',
  keywords: 'kampen overzicht, kinderkampen, zomerkampen, sportkampen, vakantiekampen, België, speelfabrik',
})

// Branded OG image with custom background
defineOgImage('SpeelFabrik', {
  title: 'KampFabrik',
  description: 'Ontdek al onze kampen voor kinderen en jongeren.',
  backgroundImage: '/images/kampen.png',
})

// Breadcrumb structured data
useSchemaOrg([
  getBreadcrumbSchema([
    { name: 'Home', path: '/' },
    { name: 'Kampen', path: '/kampen' },
  ]),
])

const route = useRoute()
const { getAgeGroups } = useAgeGroups()
const { getPeriods } = usePeriods()
const { getCategories } = useCampCategories()

// Filter state
const filters = ref<CampFilters>({})
const filterComponent = ref()

// Load filter data to map IDs
const ageGroups = ref<AgeGroup[]>([])
const periods = ref<Period[]>([])
const categories = ref<Category[]>([])

const loadFilterData = async () => {
  try {
    const [ageGroupsResponse, periodsResponse, categoriesResponse] = await Promise.all([
      getAgeGroups(true),
      getPeriods(true),
      getCategories({ isActive: true, isPublic: true }),
    ] as const)

    ageGroups.value = (ageGroupsResponse.data || []) as AgeGroup[]
    periods.value = (periodsResponse.data || []) as Period[]
    categories.value = (categoriesResponse.data || []) as Category[]
  }
  catch (error) {
    logApiError('/api/filters', error, { context: 'loading age groups, periods, categories' })
  }
}

// Apply filters from route query params on mount
const applyRouteFilters = () => {
  const queryFilters: CampFilters = {}

  // Check if there's a filter type in query (age, periode, category, or location)
  if (route.query.age) {
    const ageId = route.query.age as string
    const ageGroup = ageGroups.value.find(ag => ag.id === ageId)
    if (ageGroup)
      queryFilters.age = ageId
  }

  if (route.query.periode) {
    const periodeId = route.query.periode as string
    const period = periods.value.find(p => p.id === periodeId)
    if (period)
      queryFilters.periode = periodeId
  }

  if (route.query.category) {
    const categoryId = route.query.category as string
    const category = categories.value.find(c => c.id === categoryId)
    if (category)
      queryFilters.category = categoryId
  }

  if (route.query.location) {
    const locationId = route.query.location as string

    queryFilters.location = locationId
  }

  if (Object.keys(queryFilters).length > 0)
    filters.value = queryFilters
}

const handleFilterUpdate = (filterData: CampFilters) => {
  filters.value = filterData
}

// Watch for route changes to update filters
watch(() => route.query, () => {
  applyRouteFilters()
}, { deep: true })

// Load data and apply filters on mount
onMounted(async () => {
  await loadFilterData()
  applyRouteFilters()
})
</script>

<template>
  <VContainer>
    <VCard class="mb-8">
      <VCardText class="text-body-1 text-h6 text-center pa-4" style="line-height: 1.7;">
        Welkom in onze KampFabrik, waar plezier en fantasie de hoofdrol spelen. Wij organiseren kampen voor kinderen van de instapklas (mits schoolervaring en zindelijk) tot en met het 4de leerjaar. Om elk kind de beste ervaring te bieden, delen we de kinderen op in leeftijdsgerichte groepen, zodat de activiteiten perfect aansluiten bij hun belevingswereld.
        Onze kampen worden begeleid door gediplomeerde lesgevers, die met zorg, enthousiasme en expertise zorgen voor een warme en veilige omgeving. Zo krijgen kinderen de ruimte om zichzelf te zijn, nieuwe dingen te proberen en vooral samen heel veel plezier te maken.
      </VCardText>
    </VCard>

    <!-- Search/Filter Bar -->
    <div class="mb-6">
      <CampSearchFilter
        ref="filterComponent"
        :initial-filters="filters"
        @update:filters="handleFilterUpdate"
      />
    </div>

    <!-- Camps Listing -->
    <CampsListing :filters="filters" />
  </VContainer>
</template>
